import { SosCancelKind, SosEventType } from "@somewear/api";
import type { IMessage } from "@somewear/model";

enum SosLifecycleState {
	UNKNOWN,
	ACTIVE,
	CANCELLED_BY_ADMIN,
	CANCELLED_BY_HARDWARE,
}

/**
 * Backend SOS event delivery is at-least-once (e.g. device/firmware retransmission over
 * unreliable satellite backhaul), so a single logical alarm/cancel can surface as more than one
 * message. Rebuild each sender/session lifecycle in timestamp order. An admin cancel may be
 * followed by a new alarm for the same hardware session, while a hardware (or legacy unspecified)
 * cancel is terminal and makes later alarms stale. Non-SOS messages pass through untouched.
 */
export function collapseSosMessages(messages: IMessage[]): IMessage[] {
	const sosMessagesBySession = new Map<string, Array<{ message: IMessage; index: number }>>();
	const otherMessages: IMessage[] = [];

	for (const [index, message] of messages.entries()) {
		if (!message.sosSessionId) {
			otherMessages.push(message);
			continue;
		}

		const key = `${message.senderId}:${message.sosSessionId}`;
		const sessionMessages = sosMessagesBySession.get(key) ?? [];
		const nextSessionMessages = sessionMessages.concat([{ message, index }]);
		sosMessagesBySession.set(key, nextSessionMessages);
	}

	const collapsedSosMessages: IMessage[] = [];
	for (const sessionMessages of sosMessagesBySession.values()) {
		sessionMessages.sort((a, b) => {
			const secondsDifference =
				(a.message.timestamp?.seconds ?? 0) - (b.message.timestamp?.seconds ?? 0);
			if (secondsDifference !== 0) return secondsDifference;

			const nanosDifference =
				(a.message.timestamp?.nanos ?? 0) - (b.message.timestamp?.nanos ?? 0);
			return nanosDifference !== 0 ? nanosDifference : a.index - b.index;
		});

		let state = SosLifecycleState.UNKNOWN;
		for (const { message } of sessionMessages) {
			if (message.sosEventType === SosEventType.SOSEVENTTYPEALARM) {
				if (
					state === SosLifecycleState.UNKNOWN ||
					state === SosLifecycleState.CANCELLED_BY_ADMIN
				) {
					collapsedSosMessages.push(message);
					state = SosLifecycleState.ACTIVE;
				}
				continue;
			}

			if (message.sosEventType === SosEventType.SOSEVENTTYPECANCEL) {
				const isAdminCancel = message.sosCancelKind === SosCancelKind.SOSCANCELKINDADMIN;

				if (isAdminCancel && state !== SosLifecycleState.CANCELLED_BY_HARDWARE) {
					if (state !== SosLifecycleState.CANCELLED_BY_ADMIN) {
						collapsedSosMessages.push(message);
					}
					state = SosLifecycleState.CANCELLED_BY_ADMIN;
				} else if (state !== SosLifecycleState.CANCELLED_BY_HARDWARE) {
					collapsedSosMessages.push(message);
					state = SosLifecycleState.CANCELLED_BY_HARDWARE;
				}
				continue;
			}

			// Preserve an unknown future SOS event without letting it corrupt known lifecycle state.
			if (state !== SosLifecycleState.CANCELLED_BY_HARDWARE) {
				collapsedSosMessages.push(message);
			}
		}
	}

	return [...otherMessages, ...collapsedSosMessages];
}
